import {gql} from '@apollo/client';
import {useMemo} from 'react';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';

import {ListWarehouseModelsQuery} from './__generated/ListWarehouseModelsQuery';
import {
  UserWarehouseModelsData,
  warehouseModelGQLToRelationalData,
  WarehouseModelOptions,
} from './warehouseModelsDataLayout';

export const LIST_WAREHOUSE_MODELS_QUERY = gql`
  query ListWarehouseModelsQuery @skipAudit @engComponent(name: INSIGHTS) {
    warehouseModelListing {
      warehouseModels {
        schemaName
        tables {
          schemaName
          tableName
          perSchemaTableType
          coreTableType
          description
          warehouseUserDefinedViewMetadata {
            id
            viewQuery
            existsInWarehouse
            permissions {
              readable
              deletable
            }
          }
          schema {
            ... on SchemaInterface {
              id
            }
            ... on TagSchemaNode {
              ...TagSchemaListingGraphQL
            }
          }
          columns {
            name
            type
            isMulti
            columnRelationships {
              type
              targetColumn {
                name
                table {
                  tableName
                }
              }
            }
          }
        }
      }
    }
  }
  ${getFragmentWrapper('TagSchemaListing').fragment}
`;

export interface SuccessfulWarehouseModelsQuery {
  queryResultType: QueryResultType.SUCCESS;
  transformedData: UserWarehouseModelsData;
}
export interface NonSuccessfulWarehouseModelsQuery {
  queryResultType: Exclude<QueryResultType, QueryResultType.SUCCESS>;
}
export type WarehouseModelsQuery = SuccessfulWarehouseModelsQuery | NonSuccessfulWarehouseModelsQuery;

export async function fetchWarehouseModelsQuery(): Promise<void> {
  await doQuerySafe<ListWarehouseModelsQuery>({query: LIST_WAREHOUSE_MODELS_QUERY});
}

/**
 * A hook to access a GraphQL query fetching warehouse table data for the current user.
 *
 * After the request is successfully complete, the GraphQL data is transformed to a more friendly front-end
 * format (see warehouseModelGQLToRelationalData)
 */
export default function useWarehouseModelsQuery(
  warehouseModelOptions?: WarehouseModelOptions
): WarehouseModelsQuery {
  const request = useBenchlingQuery<ListWarehouseModelsQuery>(LIST_WAREHOUSE_MODELS_QUERY);

  const rawData = request.type === QueryResultType.SUCCESS ? request.data : null;
  const sortColumns = warehouseModelOptions?.sortColumns ?? false;
  const transformedData = useMemo(
    () => (rawData == null ? null : warehouseModelGQLToRelationalData(rawData, {sortColumns})),
    [rawData, sortColumns]
  );

  if (request.type === QueryResultType.SUCCESS) {
    return {queryResultType: request.type, transformedData: transformedData!};
  } else {
    return {queryResultType: request.type};
  }
}
